import { useEffect } from "react";
import { Box } from "@mui/material";
import { useParams } from "react-router";
import { DotLegalButton, DotLegalHeader, DotLegalPageHeader, DotLegalPaper } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { download } from "@common/icons";
import { useRiskGovernancePdfDownload } from "../riskGovernanceOverview/useRiskGovernancePdfDownload";
import { useRiskArea } from "./RiskArea.hooks";
import { buildRiskAreaViews, RiskAreaTab } from "./RiskArea.types";
import SegmentedControl from "@common/components/segmentedControl/SegmentedControl";
import RiskOperationalOverview from "../riskOperationalOverview/RiskOperationalOverview";
import { useRiskOperationalSection } from "../riskOperationalOverview/RiskOperationalOverview.hooks";
import { useBulkAssessSelection } from "../riskOperationalOverview/useBulkAssessSelection";
import { useBulkMitigateSelection } from "../riskOperationalOverview/useBulkMitigateSelection";
import { useBulkPromoteSelection } from "../riskMitigationOverview/useBulkPromoteSelection";
import RiskGovernanceOverview from "../riskGovernanceOverview/RiskGovernanceOverview";
import RiskHistoryOverview from "../riskHistoryOverview/RiskHistoryOverview";
import RiskMitigationOverview from "../riskMitigationOverview/RiskMitigationOverview";
import RiskEntityAccessBanner from "./RiskEntityAccessBanner";

function RiskArea() {
    const { translateString } = useTranslation();
    const { riskAreaId } = useParams<{ riskAreaId: string }>();
    const { riskAreaName, selectedTab, setSelectedTab } = useRiskArea();
    const bulkAssess = useBulkAssessSelection();
    const bulkMitigate = useBulkMitigateSelection();
    const bulkPromote = useBulkPromoteSelection();

    // Held here so the bulk buttons below can appear only while the entity table is the one on screen.
    const operationalSection = useRiskOperationalSection(riskAreaId);
    const governancePdfDownload = useRiskGovernancePdfDownload(riskAreaId);

    // Selection is owned here so each tab-row entry button and its table share it; clear all when the area, the
    // active tab or the operational section changes so a stale selection can't carry into a different context.
    useEffect(() => {
        bulkAssess.exitSelectionMode();
        bulkMitigate.exitSelectionMode();
        bulkPromote.exitSelectionMode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskAreaId, selectedTab, operationalSection.selectedSection]);

    // The operational table renders one checkbox column, so only one selection mode runs at a time — entering
    // one cancels the other.
    const startBulkAssess = () => {
        bulkMitigate.exitSelectionMode();
        bulkAssess.enterSelectionMode();
    };
    const startBulkMitigate = () => {
        bulkAssess.exitSelectionMode();
        bulkMitigate.enterSelectionMode();
    };

    const views = buildRiskAreaViews(translateString);

    const renderContent = () => {
        switch (selectedTab) {
            case RiskAreaTab.Governance:
                return <RiskGovernanceOverview />;
            case RiskAreaTab.Operational:
                return (
                    <RiskOperationalOverview
                        key={riskAreaId}
                        bulkAssess={bulkAssess}
                        bulkMitigate={bulkMitigate}
                        section={operationalSection}
                        sectionButtons={operationalSection.canSwitchSections ? renderOperationalButtons() : undefined}
                    />
                );
            case RiskAreaTab.History:
                return <RiskHistoryOverview />;
            case RiskAreaTab.Mitigation:
                return <RiskMitigationOverview bulkPromote={bulkPromote} />;
            default:
                return (
                    <DotLegalPaper fullHeight>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
                            <DotLegalHeader headerStyle="small" color="lightgrey">
                                {translateString("riskAreaViewComingSoon")}
                            </DotLegalHeader>
                        </Box>
                    </DotLegalPaper>
                );
        }
    };

    const renderOperationalButtons = () => {
        if (bulkAssess.isSelectionMode) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <DotLegalButton buttonType="secondary" onClick={bulkAssess.exitSelectionMode}>
                        {translateString("cancel")}
                    </DotLegalButton>
                    <DotLegalButton buttonType="primary" disabled={bulkAssess.selectedCount === 0} onClick={bulkAssess.openBulkAssessDialog}>
                        {`${translateString("assess")} (${bulkAssess.selectedCount})`}
                    </DotLegalButton>
                </Box>
            );
        }

        if (bulkMitigate.isSelectionMode) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <DotLegalButton buttonType="secondary" onClick={bulkMitigate.exitSelectionMode}>
                        {translateString("cancel")}
                    </DotLegalButton>
                    <DotLegalButton buttonType="primary" disabled={bulkMitigate.selectedCount === 0} onClick={bulkMitigate.openBulkMitigateDialog}>
                        {`${translateString("mitigate")} (${bulkMitigate.selectedCount})`}
                    </DotLegalButton>
                </Box>
            );
        }

        // The bulk entries act on the entity table, so they appear only while it is the section on screen.
        if (operationalSection.showEntities) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <DotLegalButton buttonType="secondary" onClick={startBulkAssess}>
                        {translateString("bulkAssess")}
                    </DotLegalButton>
                    <DotLegalButton buttonType="secondary" onClick={startBulkMitigate}>
                        {translateString("bulkMitigate")}
                    </DotLegalButton>
                </Box>
            );
        }

        return undefined;
    };

    const renderMitigationButtons = () => {
        if (bulkPromote.isSelectionMode) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <DotLegalButton buttonType="secondary" onClick={bulkPromote.exitSelectionMode}>
                        {translateString("cancel")}
                    </DotLegalButton>
                    <DotLegalButton buttonType="primary" disabled={bulkPromote.selectedCount === 0} onClick={bulkPromote.openConfirm}>
                        {`${translateString("promote")} (${bulkPromote.selectedCount})`}
                    </DotLegalButton>
                </Box>
            );
        }

        return (
            <DotLegalButton buttonType="secondary" onClick={bulkPromote.enterSelectionMode}>
                {translateString("bulkPromote")}
            </DotLegalButton>
        );
    };

    const renderHeaderRightComponent = () => {
        switch (selectedTab) {
            case RiskAreaTab.Operational:
                // With a section switcher the bulk buttons move down to its row, next to the entity table they act on
                return operationalSection.canSwitchSections ? undefined : renderOperationalButtons();
            case RiskAreaTab.Governance:
                return (
                    <DotLegalButton
                        buttonType="secondary"
                        startIcon={download}
                        isLoading={governancePdfDownload.isDownloading}
                        onClick={governancePdfDownload.onDownload}
                    >
                        {translateString("download")}
                    </DotLegalButton>
                );
            case RiskAreaTab.Mitigation:
                return renderMitigationButtons();
            default:
                return undefined;
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <DotLegalPageHeader
                breadCrumbs={[{ name: riskAreaName ?? "" }]}
                leftComponent={<SegmentedControl options={views} selected={selectedTab} onSelect={(value) => setSelectedTab(value)} />}
                rightComponent={renderHeaderRightComponent()}
            />

            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 2 }}>
                <RiskEntityAccessBanner riskAreaId={riskAreaId} />

                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>{renderContent()}</Box>
            </Box>
        </Box>
    );
}

export default RiskArea;
